import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import DataConnector from 'connect/DataConnector';
import {Status as AutomationFileStatusGraphQL} from 'graphql-client/__generated/graphqlGlobalTypes';
import {FromServerRunOutputSamplesTable} from 'lab-automation/output-samples/RunOutputSamplesTableConstants';
import waitForEntrySaved from 'lab-automation/utils/waitForEntrySaved';
import {FromServerResultsTable} from 'notebook/integrations/results/ResultsTableConstants';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import Tooltip from 'ui-library/bs/Tooltip';
import {GRAY} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';

import {AutomationOutputFileGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationOutputFileGraphQL';
import * as AutomationFileDAO from './AutomationFileDAO';
import {AutomationOutputFileActionType} from './LabAutomationConstants';

export const SKIP_ROWS_WITH_ERRORS_TOOLTIP =
  'If enabled, processing will continue even if there are errors. You can view all successful and failed rows after processing is complete. Note that this option is required to use file chunking when processing.';
export const SKIP_ROWS_WITH_ERRORS_LABEL = 'Skip rows with errors';

interface SkipRowsWithErrorsInputProps {
  automationOutputFile: AutomationOutputFileGraphQL;
  updateAutomationOutputFile: (
    automationOutputFile: AutomationOutputFileGraphQL,
    structuredTables?: Array<FromServerRunOutputSamplesTable | FromServerResultsTable>
  ) => void;
}
interface ConnectedProps {
  outputFileStatus: AutomationFileStatusGraphQL | null;
}

class SkipRowsWithErrorsInputConnector extends DataConnector<SkipRowsWithErrorsInputProps, ConnectedProps> {
  enhanceProps(): ConnectedProps {
    return {
      outputFileStatus: this.props.automationOutputFile.id
        ? this.reduxState().ui.automationFileStatus[this.props.automationOutputFile.id]
        : null,
    };
  }
}

function getCheckboxLabel(automationOutputFile: AutomationOutputFileGraphQL): string {
  if (BenchlingConfig.LAB_AUTOMATION_CASCADE_OUTPUT_FILE_ERRORS_TO_RELATED_ROWS) {
    const actionType = automationOutputFile.automationOutputFileConfig.automationOutputFileActionType;
    if (
      actionType === AutomationOutputFileActionType.PURE_TRANSFER ||
      actionType === AutomationOutputFileActionType.TRANSFORMATION_TRANSFER
    ) {
      return 'Skip destinations with errors';
    }
  }
  return SKIP_ROWS_WITH_ERRORS_LABEL;
}

const SkipRowsWithErrorsInput: React.FunctionComponent<SkipRowsWithErrorsInputProps> = (
  props: SkipRowsWithErrorsInputProps & ConnectedProps
): JSX.Element => {
  const [checked, setChecked] = useState(
    props.automationOutputFile?.skipRowsWithErrorsRequired ||
      props.automationOutputFile.skipRowsWithErrors ||
      false
  );
  return (
    <div className={css(skipRowsStyles.showErrorsCheckboxRow)}>
      <EditorPropertiesContext.Consumer>
        {({fileId}) => (
          <CheckboxInput
            id={`skip-rows-with-errors-${props.automationOutputFile.id}`}
            className={css(skipRowsStyles.showErrorsCheckbox)}
            // If a file is RUNNING, its frontend status is only in the Redux store, not in the backend.
            // If its SUCCEEDED or FAILED, its status is reflective of the backend status.
            disabled={
              props.outputFileStatus === AutomationFileStatusGraphQL.running ||
              props.automationOutputFile.status !== AutomationFileStatusGraphQL.pending ||
              props.automationOutputFile?.skipRowsWithErrorsRequired
            }
            label={getCheckboxLabel(props.automationOutputFile)}
            checked={checked}
            onChange={async (e: {target: {value: string}}) => {
              const skipRowsWithErrors = !checked;
              setChecked(skipRowsWithErrors);
              const saved = fileId != null ? await waitForEntrySaved(fileId) : true;
              if (saved) {
                const response = await AutomationFileDAO.updateAutomationOutputFileParams(
                  props.automationOutputFile.id,
                  {skipRowsWithErrors}
                );
                props.updateAutomationOutputFile(response.automationOutputFile);
              }
            }}
          />
        )}
      </EditorPropertiesContext.Consumer>
      <Tooltip title={SKIP_ROWS_WITH_ERRORS_TOOLTIP}>
        <BenchlingIcon className={css(skipRowsStyles.infoIcon)} icon="more-info" />
      </Tooltip>
    </div>
  );
};

export const skipRowsStyles = StyleSheet.create({
  showErrorsCheckbox: {
    alignItems: 'center',
  },
  showErrorsCheckboxRow: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '5px',
  },
  infoIcon: {
    marginLeft: '4px',
    color: GRAY,
  },
});

export default SkipRowsWithErrorsInputConnector.connect(SkipRowsWithErrorsInput);
